/**
 * Short Term Rental PDF Generator
 * Handles PDF generation specifically for Short Term Rental strategy
 */

import {
  handleImageUrl,
  formatPhoneNumber,
  getChartData,
  cashFlowAccumulationCalc,
  expensesSection,
  revenuesSection,
  appreciationSection,
  graphSection,
  revenuesSectionSTR,
} from '../utils/pdfUtils'
import { currencyFormat, percentageFormat } from '@/utils/number'
import {
  sum,
  map,
  zip,
  last,
  compact,
} from '@/utils/lodash'
import { BasePDFGenerator } from './BasePDFGenerator'
import { buildProformaDescriptionPdfNode, buildProformaDisclaimerPdfNode } from '@/utils/propertyDescription'

export class ShortTermRentalPDFGenerator extends BasePDFGenerator {
  async generatePDF() {
    return this.generateTwoPageDocDefinition()
  }

  /**
   * Generate page 1 lower right section
   */
  async generatePage1LowerRight() {
    const { property } = this
    const {
      beds = [0],
      baths = [0],
      sqft = [0],
      acquisition = {},
      analysis = {},
      assumptions = {},
      portfolio = {},
      proformaContact,
    } = property
    const { organization = {} } = portfolio

    const whiteImage = await handleImageUrl('/images/white.jpg')

    const orgEmail = proformaContact
      ? proformaContact.username
      : organization.email
        ? organization.email
        : 'Email Not Entered'
    const orgPhone = proformaContact
      ? formatPhoneNumber(proformaContact.phone)
      : organization.phone
        ? formatPhoneNumber(organization.phone)
        : 'Phone Not Entered'
    let orgLogo = property.proformaLogo
      ? await handleImageUrl(property.proformaLogo)
      : organization.logo
        ? organization.logo.url
          ? await handleImageUrl(organization.logo.url)
          : whiteImage
        : whiteImage

    if (!orgLogo) orgLogo = whiteImage

    // const IRR = acquisition.analytics.IRR
    //   ? (acquisition.analytics.IRR * 100).toFixed(2)
    //   : 0
    const IRR = acquisition.analytics.STR.IRRSTR
      ? (acquisition.analytics.STR.IRRSTR * 100).toFixed(2)
      : 0
    // const CAPRate = acquisition.analytics.capRate
    //   ? (acquisition.analytics.capRate * 100).toFixed(2)
    //   : 0
    const CAPRate = acquisition.analytics.STR.capRateSTR
      ? (acquisition.analytics.STR.capRateSTR * 100).toFixed(2)
      : 0
    const netROI = acquisition.analytics.fixFlip.netROIFixFlip
      ? (acquisition.analytics.fixFlip.netROIFixFlip * 100).toFixed(2)
      : 0
    // const cashOnCash = acquisition.analytics.cashOnCashReturn
    //   ? (acquisition.analytics.cashOnCashReturn * 100).toFixed(2)
    //   : 0
    const cashOnCash = acquisition.analytics.STR.cashOnCashReturnSTR
      ? (acquisition.analytics.STR.cashOnCashReturnSTR * 100).toFixed(
          2,
        )
      : 0
    const mortgagePayment = acquisition.analytics.STR.mortgageSTR
      ? acquisition.analytics.STR.mortgageSTR
      : 0
    const allInCost = acquisition.analytics.STR.allInCostSTR
      ? acquisition.analytics.STR.allInCostSTR
      : 0
    const utilities = acquisition.analytics.STR.utilitiesTypeDollarSTR
      ? acquisition.analytics.STR.utilitiesTypeDollarSTR
      : 0
    const assetManagementFeeTotal = acquisition.analytics.STR
      .assetManagementFeeTotalSTR
      ? acquisition.analytics.STR.assetManagementFeeTotalSTR
      : 0

    const homeInsurance = acquisition.analytics.STR
      .propertyInsuranceDollarSTR
      ? acquisition.analytics.STR.propertyInsuranceDollarSTR
      : 0
    const maintenanceDollar = acquisition.analytics.STR
      .maintenanceSTRDollar
      ? acquisition.analytics.STR.maintenanceSTRDollar
      : 0
    const rentAmount =
      assumptions.rentSTRType === 'rentRange'
        ? acquisition.analysis.rentLowSTR &&
          acquisition.analysis.rentHighSTR
          ? map(
              zip(
                acquisition.analysis.rentLowSTR,
                acquisition.analysis.rentHighSTR,
              ),
              ([low, high]) => (low + high) / 2,
            )
          : [0]
        : acquisition.analysis.rentSTR
          ? acquisition.analysis.rentSTR
          : [0]
    const avgRate = map(
      zip(assumptions.avgRate, assumptions.avgRateType),
      ([avgRate, avgRateType]) =>
        avgRateType === 'Weekly'
          ? avgRate
            ? avgRate / 7
            : 150 / 7
          : avgRate
            ? avgRate
            : 150,
    )
    const occupancyRate = assumptions.occupancyRate
      ? assumptions.occupancyRate
      : 0
    const propertyTaxes = acquisition.analysis.propertyTaxes
      ? acquisition.analysis.propertyTaxes
      : 0
    const monthlyHOA = acquisition.analytics.STR.hoaTotal
      ? acquisition.analytics.STR.hoaTotal / 12
      : 0
    const afterRepairValue = acquisition.analytics.STR.arvTotalSTR
      ? acquisition.analytics.STR.arvTotalSTR
      : 0
    // const rehabCost = acquisition.analysis.rehabCost
    //   ? acquisition.analysis.rehabCost
    //   : 0
    const rehabCost = acquisition.analytics.STR.corTotalSTR
      ? acquisition.analytics.STR.corTotalSTR
      : 0
    // const sellPrice = acquisition.analysis.sellPrice
    //   ? acquisition.analysis.afterRepairValue
    //   : 0
    const downPayment = assumptions.downPaymentSTR
      ? assumptions.downPaymentSTR
      : 0
    const rate = assumptions.rateSTR ? assumptions.rateSTR : 0
    const maintenance = assumptions.maintenanceSTR
      ? assumptions.maintenanceSTR
      : 0
    const propertyMgmtPercent = acquisition.analytics.STR
      .propertyManagementPercentageSTR
      ? acquisition.analytics.STR.propertyManagementPercentageSTR
      : 0
    const holdingPeriod = assumptions.rentalHoldingPeriodSTR
      ? assumptions.rentalHoldingPeriodSTR
      : 0

    const otherExpenses = assumptions.otherExpenseSTR
      ? assumptions.otherExpenseSTR
      : 0
    const ownerReserves = assumptions.ownerReservesSTR
      ? assumptions.ownerReservesSTR
      : 0

    const customAcquisitionExpenseTotal = acquisition.analytics
      .customAcquisitionExpenseTotal
      ? acquisition.analytics.customAcquisitionExpenseTotal
      : 0
    const customAnnualExpenseTotal = acquisition.analytics
      .customMonthlyExpenseTotal
      ? acquisition.analytics.customMonthlyExpenseTotal * 12
      : 0

    const percentOfARV = (allInCost / afterRepairValue) * 100

    const grossAvgRate = sum(
      map(
        zip(avgRate, occupancyRate),
        ([avgRate, occupancy]) => avgRate * (occupancy / 100),
      ),
    )
    const occupancyPercentage = grossAvgRate / sum(avgRate)

    const cashflowSTR = acquisition.analytics.STR.cashFlowSTR / 12
    const cashflowSTRNoFinance = cashflowSTR + mortgagePayment

    const currencyFormat = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    })

    const percentageFormat = new Intl.NumberFormat('en-US', {
      style: 'percent',
      minimumFractionDigits: 2,
    })

    const sqftString = sum(sqft)
      ? sum(sqft).toLocaleString('en')
      : '0'

    return [
      {
        text: ['Financial ', { text: 'Returns', color: '#3399ff' }],
        fontSize: 18,
        margin: [0, 5, 0, 0],
      },
      {
        canvas: [
          {
            type: 'line',
            x1: 0,
            y1: 5,
            x2: 150,
            y2: 5,
            lineWidth: 2,
          },
          {
            type: 'rect',
            x: 2,
            y: 27,
            w: 175,
            h: 55,
            r: 2,
            color: 'black',
            fillOpacity: 0.5,
          },
          {
            type: 'rect',
            x: 0,
            y: 25,
            w: 175,
            h: 55,
            r: 2,
            color: '#3399ff',
            fillOpacity: 1,
          },
          {
            type: 'rect',
            x: 192,
            y: 27,
            w: 175,
            h: 55,
            r: 2,
            color: 'black',
            fillOpacity: 0.5,
          },
          {
            type: 'rect',
            x: 190,
            y: 25,
            w: 175,
            h: 55,
            r: 2,
            color: '#344265',
            fillOpacity: 1,
          },
          {
            type: 'rect',
            x: 2,
            y: 92,
            w: 365,
            h: 92,
            r: 2,
            color: 'black',
            fillOpacity: 0.5,
          },
          {
            type: 'rect',
            x: 0,
            y: 90,
            w: 365,
            h: 92,
            r: 2,
          },
          {
            type: 'rect',
            x: 0,
            y: 90,
            w: 365,
            h: 92,
            r: 2,
            border: [true, true, true, true],
            color: 'white',
            fillOpacity: 1,
          },
          // {
          //   type: 'rect',
          //   x: 192,
          //   y: 92,
          //   w: 175,
          //   h: 92,
          //   r: 2,
          //   color: 'black',
          //   fillOpacity: 0.5,
          // },
          // {
          //   type: 'rect',
          //   x: 190,
          //   y: 90,
          //   w: 175,
          //   h: 92,
          //   r: 2,
          // },
          // {
          //   type: 'rect',
          //   x: 190,
          //   y: 90,
          //   w: 175,
          //   h: 92,
          //   r: 2,
          //   border: [true, true, true, true],
          //   color: 'white',
          //   fillOpacity: 1,
          // },
          // {
          //   type: 'rect',
          //   x: 185,
          //   y: 110,
          //   w: 85,
          //   h: 50,
          //   r: 2,
          // },
          // {
          //   type: 'rect',
          //   x: 275,
          //   y: 110,
          //   w: 85,
          //   h: 50,
          //   r: 2,
          // },
        ],
      },
      {
        absolutePosition: { x: 220, y: 360 },
        stack: [
          { text: 'Financing', fontSize: 15, color: 'white' },
          {
            canvas: [
              {
                type: 'line',
                x1: 0,
                y1: 0,
                x2: 100,
                y2: 0,
                lineWidth: 1.5,
                color: 'white',
              },
            ],
          },
          {
            text: [
              {
                text: 'Cash Flow/mo: ',
                fontSize: 12,
              },
              {
                text: currencyFormat.format(cashflowSTR),
                fontSize: 14,
                bold: true,
              },
            ],
            color: 'white',
            margin: [0, 7, 0, 0],
          },
          //{ text: 'Total ROI: ' + totalROIFinancing1.toFixed(2) + '%', color: 'white' },
        ],
      },
      {
        absolutePosition: { x: 410, y: 360 },
        stack: [
          { text: 'Cash', fontSize: 15, color: 'white' },
          {
            canvas: [
              {
                type: 'line',
                x1: 0,
                y1: 0,
                x2: 100,
                y2: 0,
                lineWidth: 1.5,
                color: 'white',
              },
            ],
          },
          {
            text: [
              {
                text: 'Cash Flow/mo: ',
                fontSize: 12,
              },
              {
                text: currencyFormat.format(cashflowSTRNoFinance),
                fontSize: 14,
                bold: true,
              },
            ],
            color: 'white',
            margin: [0, 7, 0, 0],
          },
          //{ text: 'Total ROI: ' + totalROINoFinancing1.toFixed(2) + '%', color: 'white' },
        ],
      },
      {
        absolutePosition: { x: 220, y: 425 },
        stack: [
          {
            text: ['Short Term Rental'],
            fontSize: 16,
            alignment: 'left',
          },
        ],
      },
      {
        absolutePosition: { x: 220, y: 446 },
        stack: [
          {
            text: [
              'Net Cash Flow:  ',
              {
                text: currencyFormat.format(cashflowSTR),
                fontSize: 14,
                bold: true,
              },
            ],
            fontSize: 13,
            alignment: 'left',
          },
        ],
      },
      {
        absolutePosition: { x: 220, y: 462 },
        stack: [
          {
            text: [
              'Cash on Cash:   ',
              {
                text: cashOnCash + '%',
                fontSize: 14,
                bold: true,
              },
            ],
            fontSize: 13,
            alignment: 'left',
          },
        ],
      },
      {
        absolutePosition: { x: 220, y: 478 },
        stack: [
          {
            text: [
              'IRR:  ',
              {
                text: IRR + '%',
                fontSize: 14,
                bold: true,
              },
            ],
            fontSize: 13,
            alignment: 'left',
          },
        ],
      },
      {
        absolutePosition: { x: 220, y: 494 },
        stack: [
          {
            text: [
              'Yield-on-Cost:   ',
              {
                text: CAPRate + '%',
                fontSize: 14,
                bold: true,
              },
            ],
            fontSize: 13,
            alignment: 'left',
          },
        ],
      },
      // {
      //   absolutePosition: { x: 410, y: 425 },
      //   stack: [
      //     {
      //       text: ['Fixxx & Flip'],
      //       fontSize: 16,
      //       alignment: 'left',
      //     },
      //   ],
      // },
      // {
      //   absolutePosition: { x: 410, y: 446 },
      //   stack: [
      //     {
      //       text: [
      //         'Net ROI:  ',
      //         {
      //           text: netROI + '%',
      //           fontSize: 14,
      //           bold: true,
      //         },
      //       ],
      //       fontSize: 13,
      //       alignment: 'left',
      //     },
      //   ],
      // },
      // {
      //   absolutePosition: { x: 410, y: 462 },
      //   stack: [
      //     {
      //       text: [
      //         '% of ARV:   ',
      //         {
      //           text: percentOfARV.toFixed(2) + '%',
      //           fontSize: 14,
      //           bold: true,
      //         },
      //       ],
      //       fontSize: 13,
      //       alignment: 'left',
      //     },
      //   ],
      // },
      // {
      //   absolutePosition: { x: 410, y: 478 },
      //   stack: [
      //     {
      //       text: [
      //         'Holding Time:   ',
      //         {
      //           text: holdingPeriod + ' mo.',
      //           fontSize: 14,
      //           bold: true,
      //         },
      //       ],
      //       fontSize: 13,
      //       alignment: 'left',
      //     },
      //   ],
      // },
      // {
      //   absolutePosition: { x: 410, y: 494 },
      //   stack: [
      //     {
      //       text: [
      //         'Equity:   ',
      //         {
      //           text: currencyFormat.format(equity),
      //           fontSize: 14,
      //           bold: true,
      //         },
      //       ],
      //       fontSize: 13,
      //       alignment: 'left',
      //     },
      //   ],
      // },

      {
        text: [
          'Property ',
          { text: 'Information', color: '#3399ff' },
        ],
        fontSize: 18,
        margin: [0, 20, 0, 0],
      },
      {
        canvas: [
          {
            type: 'line',
            x1: 0,
            y1: 5,
            x2: 150,
            y2: 5,
            lineWidth: 2,
          },
        ],
      },
      {
        columns: [
          {
            type: 'square',
            color: '#3399ff',
            ul: [
              {
                text: [
                  'Beds:   ',
                  { text: sum(beds), fontSize: 14, bold: true },
                ],
                color: 'black',
                fontSize: 14,
                margin: 5,
              },
              {
                text: [
                  'Baths:   ',
                  { text: sum(baths), fontSize: 14, bold: true },
                ],
                color: 'black',
                fontSize: 14,
                margin: 5,
              },
              {
                text: [
                  'Sq Feet:   ',
                  {
                    text: sqftString,
                    fontSize: 14,
                    bold: true,
                  },
                ],
                color: 'black',
                fontSize: 14,
                margin: 5,
              },
              // {
              //   text: 'Parking: ',
              //   color: 'black',
              //   fontSize: 14,
              //   margin: 5,
              // },
              // {
              //   text: 'Lot Size: ',
              //   color: 'black',
              //   fontSize: 14,
              //   margin: 5,
              // },
            ],
            margin: [0, 10, 0, 0],
            width: 140,
          },
          {
            type: 'square',
            color: '#3399ff',
            ul: [
              // {
              //   text: 'Rent Range:   ' + currencyFormat.format(rentAmount),
              //   color: 'black',
              //   fontSize: 14,
              //   margin: 5,
              // },
              {
                text: [
                  'Year:   ',
                  {
                    text: property.year,
                    fontSize: 14,
                    bold: true,
                  },
                ],
                color: 'black',
                fontSize: 14,
                margin: 5,
              },
              {
                text: [
                  'Avg Daily Rate:   ',
                  {
                    text: currencyFormat.format(sum(avgRate)),
                    fontSize: 14,
                    bold: true,
                  },
                ],
                color: 'black',
                fontSize: 14,
                margin: 5,
              },
              {
                text: [
                  'Occupancy Rate:   ',
                  {
                    text: percentageFormat.format(
                      occupancyPercentage,
                    ),
                    fontSize: 14,
                    bold: true,
                  },
                ],
                color: 'black',
                fontSize: 14,
                margin: 5,
              },
              // {
              //   text: [
              //     'Acquisition Fee:   ',
              //     {
              //       text: currencyFormat.format(
              //         acquisitionFee,
              //       ),
              //       fontSize: 14,
              //       bold: true,
              //     },
              //   ],
              //   color: 'black',
              //   fontSize: 14,
              //   margin: 5,
              // },

              // {
              //   text: 'Net ROI:   ' + netROI + '%',
              //   color: 'black',
              //   fontSize: 14,
              //   margin: 5,
              // },
              // {
              //   text: '% of ARV:   ' + percentOfARV.toFixed(2) + '%',
              //   color: 'black',
              //   fontSize: 14,
              //   margin: 5,
              // },
              // {
              //   text: 'Flip Holding Period:   ' + holdingPeriod + ' mo.',
              //   color: 'black',
              //   fontSize: 14,
              //   margin: 5,
              // },
              // {
              //   text: 'Sale Proceeds:   ' + currencyFormat.format(saleProceeds),
              //   color: 'black',
              //   fontSize: 14,
              //   margin: 5,
              // },

              // {
              //   text: 'Property Taxes:   ' + currencyFormat.format(propertyTaxes),
              //   color: 'black',
              //   fontSize: 14,
              //   margin: 5,
              // },
              // {
              //   text: 'Home Insurance:   ' + currencyFormat.format(homeInsurance),
              //   color: 'black',
              //   fontSize: 14,
              //   margin: 5,
              // },
              // {
              //   text: 'Annual HOA:   ' + currencyFormat.format(yearlyHOA),
              //   color: 'black',
              //   fontSize: 14,
              //   margin: 5,
              // },
              // {
              //   text: 'Property Mgmt (' + propertyMgmtPercent + '%):   ' + currencyFormat.format(propertyMgmt),
              //   color: 'black',
              //   fontSize: 14,
              //   margin: 5,
              //   pageBreak: 'after'
              // },
            ],
            margin: [0, 10, 0, 0],
            width: 'auto',
          },
        ],
      },
      buildProformaDescriptionPdfNode(property),
    ]
  }

  /**
   * Generate page 2 top section
   */
  async generatePage2Top() {
    const { property } = this
    const { acquisition = {}, assumptions = {} } = property

    const mortgagePayment = acquisition.analytics.STR.mortgageSTR
      ? acquisition.analytics.STR.mortgageSTR
      : 0
    const financeAmount = acquisition.analytics.STR.loanAmountSTR
      ? acquisition.analytics.STR.loanAmountSTR
      : 0
    const downPaymentTotal = acquisition.analytics.STR
      .downPaymentTotalSTR
      ? acquisition.analytics.STR.downPaymentTotalSTR
      : 0
    const pointsAmount = acquisition.analytics.STR.pointsAmountSTR
      ? acquisition.analytics.STR.pointsAmountSTR
      : 0
    const creditFromSeller = assumptions.creditFromSeller
      ? assumptions.creditFromSeller
      : 0
    const closeCostDollar = acquisition.analytics.STR
      .closeCostDollarSTR
      ? acquisition.analytics.STR.closeCostDollarSTR
      : 0
    const closeCostPercent = acquisition.analytics.STR
      .closeCostPercentSTR
      ? acquisition.analytics.STR.closeCostPercentSTR
      : 0
    const paydownMonthly = acquisition.analytics.STR.paydownMonthlySTR
      ? acquisition.analytics.STR.paydownMonthlySTR
      : 0
    const acquisitionCommissionDollar = acquisition.analytics
      .acquisitionCommissionDollar
      ? acquisition.analytics.acquisitionCommissionDollar
      : 0
    const acquisitionCommissionPercent = acquisition.analytics
      .acquisitionCommissionPercent
      ? acquisition.analytics.acquisitionCommissionPercent
      : 0
    const propertyManagementDollar = acquisition.analytics.STR
      .propertyManagementDollarSTR
      ? acquisition.analytics.STR.propertyManagementDollarSTR
      : 0
    const utilities = acquisition.analytics.STR.utilitiesTypeDollarSTR
      ? acquisition.analytics.STR.utilitiesTypeDollarSTR
      : 0
    const utilitiesPercent = assumptions.utilitiesSTR
      ? assumptions.utilitiesSTR
      : 0
    const financingFeeTotal = acquisition.analytics.STR
      .financingFeeTotalSTR
      ? acquisition.analytics.STR.financingFeeTotalSTR
      : 0
    const sellerClosingCost = assumptions.sellerClosingCostSTR
      ? assumptions.sellerClosingCostSTR
      : 0
    const salesCommissionPercent = assumptions.salesCommissionSTR
      ? assumptions.salesCommissionSTR
      : 0
    const dispositionFee = assumptions.dispositionFeeSTR
      ? assumptions.dispositionFeeSTR
      : 0
    const afterRepairValue = acquisition.analytics.STR.arvTotalSTR
      ? acquisition.analytics.STR.arvTotalSTR
      : 0

    const homePriceAppreciation = assumptions.homePriceAppreciation
      ? assumptions.homePriceAppreciation
      : 0

    const rentalHoldingPeriod = assumptions.rentalHoldingPeriodSTR
      ? assumptions.rentalHoldingPeriodSTR
      : 0

    const appreciation = appreciationSection({
      afterRepairValue,
      homePriceAppreciation,
      term: rentalHoldingPeriod * 12,
    })
    const sellPriceAppreciation =
      afterRepairValue +
      (last(appreciation) ? last(appreciation).appreciation : 0)
    const sellingClosingCostPercent = acquisition.analytics.STR
      .sellingClosingCostPercentSTR
      ? acquisition.analytics.STR.sellingClosingCostPercentSTR
      : 0
    const sellingClosingCostTotal =
      assumptions.sellerClosingCostSTRType === '$'
        ? sellerClosingCost
        : (sellerClosingCost / 100) * sellPriceAppreciation
    const dispositionFeeTotal =
      assumptions.dispositionFeeSTRType === '$'
        ? dispositionFee
        : (dispositionFee / 100) * sellPriceAppreciation
    const salesCommission =
      assumptions.salesCommissionSTRType === '$'
        ? salesCommissionPercent
        : (salesCommissionPercent / 100) * sellPriceAppreciation
    const assetManagementFeeTotal = acquisition.analytics.STR
      .assetManagementFeeTotalSTR
      ? acquisition.analytics.STR.assetManagementFeeTotalSTR
      : 0

    const homeInsurance = acquisition.analytics.STR
      .propertyInsuranceDollarSTR
      ? acquisition.analytics.STR.propertyInsuranceDollarSTR
      : 0
    const maintenanceDollar = acquisition.analytics.STR
      .maintenanceSTRDollar
      ? acquisition.analytics.STR.maintenanceSTRDollar
      : 0

    const rentAmount =
      assumptions.rentSTRType === 'rentRange'
        ? acquisition.analysis.rentLowSTR &&
          acquisition.analysis.rentHighSTR
          ? map(
              zip(
                acquisition.analysis.rentLowSTR,
                acquisition.analysis.rentHighSTR,
              ),
              ([low, high]) => (low + high) / 2,
            )
          : [0]
        : acquisition.analysis.rentSTR
          ? acquisition.analysis.rentSTR
          : [0]
    // const otherIncome = acquisition.analysis.otherIncome
    //   ? acquisition.analysis.otherIncome
    //   : [0]
    const propertyTaxes = acquisition.analysis.propertyTaxes
      ? acquisition.analysis.propertyTaxes
      : 0
    const monthlyHOA = acquisition.analytics.STR.hoaTotal
      ? acquisition.analytics.STR.hoaTotal / 12
      : 0
    const financingRepairsPercentage = acquisition.analysis
      .financingRepairsPercentageSTR
      ? acquisition.analysis.financingRepairsPercentageSTR
      : 0
    // const rehabCost = acquisition.analysis.rehabCost
    //   ? acquisition.analysis.rehabCost
    //   : 0
    const rehabCost = acquisition.analytics.STR.corTotalSTR
      ? acquisition.analytics.STR.corTotalSTR
      : 0
    // const sellPrice = acquisition.analysis.sellPrice
    //   ? acquisition.analysis.afterRepairValue
    //   : 0
    const downPayment = assumptions.downPaymentSTR
      ? assumptions.downPaymentSTR
      : 0
    const rate = assumptions.rateSTR ? assumptions.rateSTR : 0
    const term = assumptions.termSTR ? assumptions.termSTR : 0
    const termType = assumptions.termSTRType
      ? assumptions.termSTRType
      : 'Months'
    const vacancy = assumptions.vacancy ? assumptions.vacancy : 0
    const maintenance = assumptions.maintenanceSTR
      ? assumptions.maintenanceSTR
      : 0
    const propertyMgmtPercent = acquisition.analytics.STR
      .propertyManagementPercentageSTR
      ? acquisition.analytics.STR.propertyManagementPercentageSTR
      : 0
    const pointPercentage = assumptions.pointsSTR
      ? assumptions.pointsSTR
      : 0
    const payDownAccelerator = assumptions.payDownAccelerator
      ? assumptions.payDownAccelerator
      : 0
    const annualRevenueIncrease = assumptions.growthRateIncome
      ? assumptions.growthRateIncome
      : 0
    const annualOperatingExpenseIncrease =
      assumptions.growthRateExpense
        ? assumptions.growthRateExpense
        : 0
    const acquisitionFee = assumptions.acquisitionFee
      ? assumptions.acquisitionFee
      : 0
    const financingFee = acquisition.analytics.STR
      .financingFeePercentSTR
      ? acquisition.analytics.STR.financingFeePercentSTR
      : 0

    const otherExpenses = assumptions.otherExpenseSTR
      ? assumptions.otherExpenseSTR
      : 0

    const customAcquisitionExpenseTotal = acquisition.analytics
      .customAcquisitionExpenseTotal
      ? acquisition.analytics.customAcquisitionExpenseTotal
      : 0
    const customAnnualExpenseTotal = acquisition.analytics
      .customMonthlyExpenseTotal
      ? acquisition.analytics.customMonthlyExpenseTotal * 12
      : 0

    const monthlyRate = rate / 12 / 100
    const financedRenovationAmount =
      rehabCost - rehabCost * (downPayment / 100) || 0
    const renovationAmount = rehabCost || 0

    const graphTest = graphSection({
      financeAmount,
      mortgagePayment,
      monthlyRate,
      debtYear: termType === 'Years' ? term * 12 : term,
      paydownMonthly,
      //holdingPeriod,
      holdingPeriod: false,
    })
    const loanBalanceAtHoldingPeriod = graphSection({
      financeAmount,
      mortgagePayment,
      monthlyRate,
      debtYear: rentalHoldingPeriod * 12,
      paydownMonthly,
      holdingPeriod: true,
    })

    const totalInitialInvestmentFinancing =
      downPaymentTotal +
      acquisitionCommissionDollar +
      acquisitionFee +
      closeCostDollar +
      financingFeeTotal +
      pointsAmount -
      creditFromSeller +
      renovationAmount * (1 - financingRepairsPercentage / 100) +
      customAcquisitionExpenseTotal
    const yearlyHOA = monthlyHOA * 12
    const rentalIncome3 = revenuesSection({
      revenueYear: 3,
      rentAmount: rentAmount,
      annualRevenueIncrease: annualRevenueIncrease / 100,
    })
    const rentalIncome5 = revenuesSection({
      revenueYear: 5,
      rentAmount: rentAmount,
      annualRevenueIncrease: annualRevenueIncrease / 100,
    })
    const rentalIncome10 = revenuesSection({
      revenueYear: 10,
      rentAmount: rentAmount,
      annualRevenueIncrease: annualRevenueIncrease / 100,
    })

    const vacancyRateMonthly = sum(
      map(
        zip(rentAmount, vacancy),
        ([rentAmount, vacancy]) => rentAmount * (vacancy / 100),
      ),
    )
    const vacancyRate1 =
      sum(
        map(
          zip(rentAmount, vacancy),
          ([rentAmount, vacancy]) => rentAmount * (vacancy / 100),
        ),
      ) * 12
    const vacancyRate3 = sum(
      map(
        zip(rentalIncome3, vacancy),
        ([rentalIncome3, vacancy]) => rentalIncome3 * (vacancy / 100),
      ),
    )
    const vacancyRate5 = sum(
      map(
        zip(rentalIncome5, vacancy),
        ([rentalIncome5, vacancy]) => rentalIncome5 * (vacancy / 100),
      ),
    )
    const vacancyRate10 = sum(
      map(
        zip(rentalIncome10, vacancy),
        ([rentalIncome10, vacancy]) =>
          rentalIncome10 * (vacancy / 100),
      ),
    )
    const grossRentalIncomeMonthly =
      sum(rentAmount) - vacancyRateMonthly
    const grossRentalIncome1 = sum(rentAmount) * 12 - vacancyRate1
    const grossRentalIncome3 = sum(rentalIncome3) - vacancyRate3
    const grossRentalIncome5 = sum(rentalIncome5) - vacancyRate5
    const grossRentalIncome10 = sum(rentalIncome10) - vacancyRate10
    const propertyTaxes3 = expensesSection({
      expenseType: 'PT',
      revenueYear: 3,
      propertyTaxes,
      annualOperatingExpenseIncrease:
        annualOperatingExpenseIncrease / 100,
    })
    const propertyTaxes5 = expensesSection({
      expenseType: 'PT',
      revenueYear: 5,
      propertyTaxes,
      annualOperatingExpenseIncrease:
        annualOperatingExpenseIncrease / 100,
    })
    const propertyTaxes10 = expensesSection({
      expenseType: 'PT',
      revenueYear: 10,
      propertyTaxes,
      annualOperatingExpenseIncrease:
        annualOperatingExpenseIncrease / 100,
    })
    const homeInsurance3 = expensesSection({
      expenseType: 'INSURANCE',
      revenueYear: 3,
      homeInsurance,
      annualOperatingExpenseIncrease:
        annualOperatingExpenseIncrease / 100,
    })
    const homeInsurance5 = expensesSection({
      expenseType: 'INSURANCE',
      revenueYear: 5,
      homeInsurance,
      annualOperatingExpenseIncrease:
        annualOperatingExpenseIncrease / 100,
    })
    const homeInsurance10 = expensesSection({
      expenseType: 'INSURANCE',
      revenueYear: 10,
      homeInsurance,
      annualOperatingExpenseIncrease:
        annualOperatingExpenseIncrease / 100,
    })
    const HOA3 = expensesSection({
      expenseType: 'HOA',
      revenueYear: 3,
      yearlyHOA,
      annualOperatingExpenseIncrease:
        annualOperatingExpenseIncrease / 100,
    })
    const HOA5 = expensesSection({
      expenseType: 'HOA',
      revenueYear: 5,
      yearlyHOA,
      annualOperatingExpenseIncrease:
        annualOperatingExpenseIncrease / 100,
    })
    const HOA10 = expensesSection({
      expenseType: 'HOA',
      revenueYear: 10,
      yearlyHOA,
      annualOperatingExpenseIncrease:
        annualOperatingExpenseIncrease / 100,
    })
    const propertyManagement1 = propertyManagementDollar
    const propertyManagement3 =
      assumptions.propertyManagementSTRType === '$'
        ? propertyManagementDollar
        : sum(rentalIncome3) * (propertyMgmtPercent / 100)
    const propertyManagement5 =
      assumptions.propertyManagementSTRType === '$'
        ? propertyManagementDollar
        : sum(rentalIncome5) * (propertyMgmtPercent / 100)
    const propertyManagement10 =
      assumptions.propertyManagementSTRType === '$'
        ? propertyManagementDollar
        : sum(rentalIncome10) * (propertyMgmtPercent / 100)
    const maintenance1 = maintenanceDollar
    const maintenance3 = expensesSection({
      expenseType: 'MAINTENANCE',
      revenueYear: 3,
      rentAmount: grossRentalIncome1,
      maintenance: maintenance / 100,
      annualOperatingExpenseIncrease:
        annualOperatingExpenseIncrease / 100,
    })
    const maintenance5 = expensesSection({
      expenseType: 'MAINTENANCE',
      revenueYear: 5,
      rentAmount: grossRentalIncome1,
      maintenance: maintenance / 100,
      annualOperatingExpenseIncrease:
        annualOperatingExpenseIncrease / 100,
    })
    const maintenance10 = expensesSection({
      expenseType: 'MAINTENANCE',
      revenueYear: 10,
      rentAmount: grossRentalIncome1,
      maintenance: maintenance / 100,
      annualOperatingExpenseIncrease:
        annualOperatingExpenseIncrease / 100,
    })
    const utilities1 = utilities * 12
    const utilities3 =
      assumptions.utilitiesSTRType === '$'
        ? utilities * 12
        : sum(rentalIncome3) * (utilitiesPercent / 100)
    const utilities5 =
      assumptions.utilitiesSTRType === '$'
        ? utilities * 12
        : sum(rentalIncome5) * (utilitiesPercent / 100)
    const utilities10 =
      assumptions.utilitiesSTRType === '$'
        ? utilities * 12
        : sum(rentalIncome10) * (utilitiesPercent / 100)
    const assetFee1 = expensesSection({
      expenseType: 'ASSET',
      revenueYear: 1,
      rentAmount: grossRentalIncome1,
      assetManagementFeeTotal: assetManagementFeeTotal,
      totalInitialInvestmentFinancing,
    })
    const assetFee3 = expensesSection({
      expenseType: 'ASSET',
      revenueYear: 3,
      rentAmount: grossRentalIncome1,
      assetManagementFeeTotal: assetManagementFeeTotal,
      totalInitialInvestmentFinancing,
    })
    const assetFee5 = expensesSection({
      expenseType: 'ASSET',
      revenueYear: 5,
      rentAmount: grossRentalIncome1,
      assetManagementFeeTotal: assetManagementFeeTotal,
      totalInitialInvestmentFinancing,
    })
    const assetFee10 = expensesSection({
      expenseType: 'ASSET',
      revenueYear: 10,
      rentAmount: grossRentalIncome1,
      assetManagementFeeTotal: assetManagementFeeTotal,
      totalInitialInvestmentFinancing,
    })
    const otherExpense3 = expensesSection({
      expenseType: 'OTHER',
      revenueYear: 3,
      rentAmount: grossRentalIncome1,
      otherExpenses,
      annualOperatingExpenseIncrease:
        annualOperatingExpenseIncrease / 100,
    })
    const otherExpense5 = expensesSection({
      expenseType: 'OTHER',
      revenueYear: 5,
      rentAmount: grossRentalIncome1,
      otherExpenses,
      annualOperatingExpenseIncrease:
        annualOperatingExpenseIncrease / 100,
    })
    const otherExpense10 = expensesSection({
      expenseType: 'OTHER',
      revenueYear: 10,
      rentAmount: grossRentalIncome1,
      otherExpenses,
      annualOperatingExpenseIncrease:
        annualOperatingExpenseIncrease / 100,
    })
    const customAnnualExpenseTotal3 = expensesSection({
      expenseType: 'CUSTOM',
      revenueYear: 3,
      rentAmount: grossRentalIncome1,
      customAnnualExpenseTotal,
      annualOperatingExpenseIncrease:
        annualOperatingExpenseIncrease / 100,
    })
    const customAnnualExpenseTotal5 = expensesSection({
      expenseType: 'CUSTOM',
      revenueYear: 5,
      rentAmount: grossRentalIncome1,
      customAnnualExpenseTotal,
      annualOperatingExpenseIncrease:
        annualOperatingExpenseIncrease / 100,
    })
    const customAnnualExpenseTotal10 = expensesSection({
      expenseType: 'CUSTOM',
      revenueYear: 10,
      rentAmount: grossRentalIncome1,
      customAnnualExpenseTotal,
      annualOperatingExpenseIncrease:
        annualOperatingExpenseIncrease / 100,
    })
    const saleProceeds =
      sellPriceAppreciation -
      salesCommission -
      sellingClosingCostTotal -
      loanBalanceAtHoldingPeriod -
      totalInitialInvestmentFinancing -
      dispositionFeeTotal
    const totalExpenseMonthly =
      propertyTaxes / 12 +
      homeInsurance / 12 +
      monthlyHOA +
      (propertyMgmtPercent / 100) * grossRentalIncomeMonthly +
      (maintenance / 100) * grossRentalIncomeMonthly +
      utilities +
      assetManagementFeeTotal / 12 +
      otherExpenses / 12 +
      customAnnualExpenseTotal / 12
    const totalExpense1 =
      propertyTaxes +
      homeInsurance +
      yearlyHOA +
      propertyManagement1 +
      maintenance1 +
      utilities1 +
      assetFee1 +
      otherExpenses +
      customAnnualExpenseTotal
    const totalExpense3 =
      propertyTaxes3 +
      homeInsurance3 +
      HOA3 +
      propertyManagement3 +
      maintenance3 +
      utilities3 +
      assetFee3 +
      otherExpense3 +
      customAnnualExpenseTotal3
    const totalExpense5 =
      propertyTaxes5 +
      homeInsurance5 +
      HOA5 +
      propertyManagement5 +
      maintenance5 +
      utilities5 +
      assetFee5 +
      otherExpense5 +
      customAnnualExpenseTotal5
    const totalExpense10 =
      propertyTaxes10 +
      homeInsurance10 +
      HOA10 +
      propertyManagement10 +
      maintenance10 +
      utilities10 +
      assetFee10 +
      otherExpense10 +
      customAnnualExpenseTotal10
    const NOINoFinanceMonthly =
      grossRentalIncomeMonthly - totalExpenseMonthly
    const NOINoFinance1 = grossRentalIncome1 - totalExpense1
    const NOINoFinance3 = grossRentalIncome3 - totalExpense3
    const NOINoFinance5 = grossRentalIncome5 - totalExpense5
    const NOINoFinance10 = grossRentalIncome10 - totalExpense10
    const mortgagePaymentYearly = mortgagePayment * 12
    const totalReturnFinancing = []
    const totalReturnFinancing1 =
      NOINoFinance1 - mortgagePaymentYearly
    const totalReturnFinancing3 =
      NOINoFinance3 - mortgagePaymentYearly
    const totalReturnFinancing5 =
      NOINoFinance5 - mortgagePaymentYearly
    const totalReturnFinancing10 =
      NOINoFinance10 - mortgagePaymentYearly
    totalReturnFinancing.push({
      one: totalReturnFinancing1,
      three: totalReturnFinancing3,
      five: totalReturnFinancing5,
      ten: totalReturnFinancing10,
    })

    const cashFlowAccumulation = cashFlowAccumulationCalc({
      accelerator: payDownAccelerator,
      term: termType === 'Years' ? term * 12 + 1 : term + 1,
      rentAmount: rentAmount || [0],
      vacancy: vacancy
        ? map(vacancy, (vacancy) => vacancy / 100)
        : [0],
      propertyTaxes: propertyTaxes / 12,
      homeInsurance: homeInsurance / 12,
      monthlyHOA,
      propertyMgmtPercent: propertyMgmtPercent / 100,
      maintenance: maintenance / 100,
      utilities: utilities,
      annualRevenueIncrease: annualRevenueIncrease / 100,
      annualOperatingExpenseIncrease:
        annualOperatingExpenseIncrease / 100,
      mortgagePayment,
    })
    const graphDataURL = getChartData(
      property,
      graphTest,
      totalReturnFinancing,
      appreciation,
      cashFlowAccumulation,
      totalInitialInvestmentFinancing,
    )

    return [
      {
        width: 400,
        stack: [
          {
            text: [
              'Financial ',
              { text: 'Projections', color: '#3399ff' },
            ],
            fontSize: 18,
            margin: [15, 20, 0, 0],
          },
          {
            canvas: [
              {
                type: 'line',
                x1: 15,
                y1: 5,
                x2: 150,
                y2: 5,
                lineWidth: 2,
              },
            ],
          },
          {
            image: graphDataURL,
            width: 400,
            height: 275,
            margin: [10, 20, 0, 0],
          },
        ],
      },
      [
        {
          margin: [20, 30],
          stack: [
            {
              table: {
                headerRows: 1,
                widths: ['*', '*'],
                body: [
                  [
                    {
                      text: 'Financing Assumptions',
                      fillColor: '#3399ff',
                      color: 'white',
                      fontSize: 8,
                      colSpan: 2,
                      alignment: 'center',
                    },
                    { text: '' },
                  ],
                  [
                    {
                      text: 'Loan Amount',
                      alignment: 'left',
                      fontSize: 6,
                    },
                    {
                      text: currencyFormat.format(financeAmount),
                      alignment: 'right',
                      fontSize: 6,
                    },
                  ],
                  [
                    {
                      text: 'Financed Rehab Amount',
                      alignment: 'left',
                      fontSize: 6,
                    },
                    {
                      text: currencyFormat.format(
                        (financedRenovationAmount *
                          financingRepairsPercentage) /
                          100,
                      ),
                      alignment: 'right',
                      fontSize: 6,
                    },
                  ],
                  [
                    {
                      text: 'Interest Rate',
                      alignment: 'left',
                      fontSize: 6,
                    },
                    {
                      text: rate + '%',
                      alignment: 'right',
                      fontSize: 6,
                    },
                  ],
                  [
                    {
                      text: 'Mortgage (yrs)',
                      alignment: 'left',
                      fontSize: 6,
                    },
                    {
                      text: (termType === 'Years'
                        ? term
                        : term / 12
                      ).toFixed(1),
                      alignment: 'right',
                      fontSize: 6,
                    },
                  ],
                  [
                    {
                      text: 'Mortgage Payment',
                      alignment: 'left',
                      fontSize: 6,
                    },
                    {
                      text: currencyFormat.format(mortgagePayment),
                      alignment: 'right',
                      fontSize: 6,
                    },
                  ],
                  [
                    {
                      text:
                        'Paydown Acc. (' + payDownAccelerator + '%)',
                      alignment: 'left',
                      fontSize: 6,
                    },
                    {
                      text: currencyFormat.format(paydownMonthly),
                      alignment: 'right',
                      fontSize: 6,
                    },
                  ],
                ],
              },
              layout: {
                defaultBorder: false,
              },
            },
            {
              table: {
                headerRows: 1,
                widths: ['*', '*'],
                body: compact([
                  [
                    {
                      text: 'Initial Investment',
                      fillColor: '#3399ff',
                      color: 'white',
                      fontSize: 8,
                      colSpan: 2,
                      alignment: 'center',
                    },
                    { text: '' },
                  ],
                  downPaymentTotal
                    ? [
                        {
                          text: 'Down Payment (' + downPayment + '%)',
                          alignment: 'left',
                          fontSize: 6,
                        },
                        {
                          text: currencyFormat.format(
                            downPaymentTotal,
                          ),
                          alignment: 'right',
                          fontSize: 6,
                        },
                      ]
                    : null,
                  creditFromSeller
                    ? [
                        {
                          text: '-Credit From Seller',
                          alignment: 'left',
                          fontSize: 6,
                        },
                        {
                          text: currencyFormat.format(
                            -1 * creditFromSeller,
                          ),
                          alignment: 'right',
                          fontSize: 6,
                        },
                      ]
                    : null,
                  renovationAmount
                    ? [
                        {
                          text: 'Rehab Amount',
                          alignment: 'left',
                          fontSize: 6,
                        },
                        {
                          text: currencyFormat.format(
                            renovationAmount *
                              (1 - financingRepairsPercentage / 100),
                          ),
                          alignment: 'right',
                          fontSize: 6,
                        },
                      ]
                    : null,
                  acquisitionFee
                    ? [
                        {
                          text: 'Acquisition Fee',
                          alignment: 'left',
                          fontSize: 6,
                        },
                        {
                          text: currencyFormat.format(acquisitionFee),
                          alignment: 'right',
                          fontSize: 6,
                        },
                      ]
                    : null,
                  acquisitionCommissionDollar
                    ? [
                        {
                          text:
                            'Acquisition Commission (' +
                            acquisitionCommissionPercent +
                            '%)',
                          alignment: 'left',
                          fontSize: 6,
                        },
                        {
                          text: currencyFormat.format(
                            acquisitionCommissionDollar,
                          ),
                          alignment: 'right',
                          fontSize: 6,
                        },
                      ]
                    : null,
                  closeCostDollar
                    ? [
                        {
                          text:
                            'Est Closing Cost (' +
                            closeCostPercent +
                            '%)',
                          alignment: 'left',
                          fontSize: 6,
                        },
                        {
                          text: currencyFormat.format(
                            closeCostDollar,
                          ),
                          alignment: 'right',
                          fontSize: 6,
                        },
                      ]
                    : null,
                  financingFeeTotal
                    ? [
                        {
                          text:
                            'Financing Fee (' + financingFee + '%)',
                          alignment: 'left',
                          fontSize: 6,
                        },
                        {
                          text: currencyFormat.format(
                            financingFeeTotal,
                          ),
                          alignment: 'right',
                          fontSize: 6,
                        },
                      ]
                    : null,
                  customAcquisitionExpenseTotal
                    ? [
                        {
                          text: 'Custom Expenses',
                          alignment: 'left',
                          fontSize: 6,
                        },
                        {
                          text: currencyFormat.format(
                            customAcquisitionExpenseTotal,
                          ),
                          alignment: 'right',
                          fontSize: 6,
                        },
                      ]
                    : null,
                  pointsAmount
                    ? [
                        {
                          text: 'Points (' + pointPercentage + '%)',
                          alignment: 'left',
                          fontSize: 6,
                        },
                        {
                          text: currencyFormat.format(pointsAmount),
                          alignment: 'right',
                          fontSize: 6,
                        },
                      ]
                    : null,
                  [
                    {
                      text: 'Total Out Of Pocket',
                      alignment: 'left',
                      fontSize: 6,
                      bold: true,
                      border: [false, true, false, false],
                    },
                    {
                      text: currencyFormat.format(
                        totalInitialInvestmentFinancing,
                      ),
                      alignment: 'right',
                      fontSize: 6,
                      bold: true,
                      border: [false, true, false, false],
                    },
                  ],
                ]),
              },
              layout: {
                defaultBorder: false,
              },
              margin: [0, 10, 0, 0],
            },
            {
              table: {
                headerRows: 1,
                widths: ['*', '*'],
                body: compact([
                  [
                    {
                      text: 'Net Profit',
                      fillColor: '#3399ff',
                      color: 'white',
                      fontSize: 8,
                      colSpan: 2,
                      alignment: 'center',
                    },
                    { text: '' },
                  ],
                  sellPriceAppreciation
                    ? [
                        {
                          text: 'Sell Price at holding Period',
                          alignment: 'left',
                          fontSize: 6,
                        },
                        {
                          text: currencyFormat.format(
                            sellPriceAppreciation,
                          ),
                          alignment: 'right',
                          fontSize: 6,
                        },
                      ]
                    : null,
                  salesCommission
                    ? [
                        {
                          text:
                            'Sales Commission (' +
                            (assumptions.salesCommissionSTRType ===
                            '$'
                              ? currencyFormat.format(
                                  salesCommissionPercent,
                                ) + ')'
                              : salesCommissionPercent + '%)'),
                          alignment: 'left',
                          fontSize: 6,
                        },
                        {
                          text: currencyFormat.format(
                            salesCommission,
                          ),
                          alignment: 'right',
                          fontSize: 6,
                        },
                      ]
                    : null,
                  sellingClosingCostTotal
                    ? [
                        {
                          text:
                            'Seller Closing Cost (' +
                            sellingClosingCostPercent +
                            '%)',
                          alignment: 'left',
                          fontSize: 6,
                        },
                        {
                          text: currencyFormat.format(
                            sellingClosingCostTotal,
                          ),
                          alignment: 'right',
                          fontSize: 6,
                        },
                      ]
                    : null,
                  dispositionFeeTotal
                    ? [
                        {
                          text: 'Disposition Fee',
                          alignment: 'left',
                          fontSize: 6,
                        },
                        {
                          text: currencyFormat.format(
                            dispositionFeeTotal,
                          ),
                          alignment: 'right',
                          fontSize: 6,
                        },
                      ]
                    : null,
                  totalInitialInvestmentFinancing
                    ? [
                        {
                          text: 'Total Out Of Pocket',
                          alignment: 'left',
                          fontSize: 6,
                        },
                        {
                          text: currencyFormat.format(
                            totalInitialInvestmentFinancing,
                          ),
                          alignment: 'right',
                          fontSize: 6,
                        },
                      ]
                    : null,
                  loanBalanceAtHoldingPeriod
                    ? [
                        {
                          text: 'Loan Balance at Holding Period',
                          alignment: 'left',
                          fontSize: 6,
                        },
                        {
                          text: currencyFormat.format(
                            loanBalanceAtHoldingPeriod,
                          ),
                          alignment: 'right',
                          fontSize: 6,
                        },
                      ]
                    : null,
                  [
                    {
                      text: 'Net Profit',
                      alignment: 'left',
                      fontSize: 6,
                      bold: true,
                      border: [false, true, false, false],
                    },
                    {
                      text: currencyFormat.format(saleProceeds),
                      alignment: 'right',
                      fontSize: 6,
                      bold: true,
                      border: [false, true, false, false],
                    },
                  ],
                ]),
              },
              layout: {
                defaultBorder: false,
              },
              margin: [0, 10, 0, 0],
            },
          ],
        },
      ],
    ]
  }

  /**
   * Generate page 2 bottom section
   */
  async generatePage2Bottom() {
    const { property } = this

    const {
      acquisition = {},
      assumptions = {},
      portfolio = {},
    } = property
    const { organization = {} } = portfolio
    const { legalDescription = '' } = organization
    const disclaimerNode = buildProformaDisclaimerPdfNode(legalDescription)

    const avgRate = map(
      zip(assumptions.avgRate, assumptions.avgRateType),
      ([avgRate, avgRateType]) =>
        avgRateType === 'Weekly'
          ? avgRate
            ? avgRate / 7
            : 150 / 7
          : avgRate
            ? avgRate
            : 150,
    )
    const mortgagePayment = acquisition.analytics.STR.mortgageSTR
      ? acquisition.analytics.STR.mortgageSTR
      : 0
    const downPaymentTotal = acquisition.analytics.STR
      .downPaymentTotalSTR
      ? acquisition.analytics.STR.downPaymentTotalSTR
      : 0
    const pointsAmount = acquisition.analytics.STR.pointsAmountSTR
      ? acquisition.analytics.STR.pointsAmountSTR
      : 0
    const creditFromSeller = assumptions.creditFromSeller
      ? assumptions.creditFromSeller
      : 0
    const closeCostDollar = acquisition.analytics.STR
      .closeCostDollarSTR
      ? acquisition.analytics.STR.closeCostDollarSTR
      : 0
    const acquisitionCommissionDollar = acquisition.analytics
      .acquisitionCommissionDollar
      ? acquisition.analytics.acquisitionCommissionDollar
      : 0
    const cohostingDollar = acquisition.analytics.STR.cohostingDollar
      ? acquisition.analytics.STR.cohostingDollar / 12
      : 0
    const utilities = acquisition.analytics.STR.utilitiesTypeDollarSTR
      ? acquisition.analytics.STR.utilitiesTypeDollarSTR
      : 0
    const utilitiesPercent = assumptions.utilitiesSTR
      ? assumptions.utilitiesSTR
      : 0
    const financingFeeTotal = acquisition.analytics.STR
      .financingFeeTotal
      ? acquisition.analytics.STR.financingFeeTotal
      : 0
    const assetManagementFeeTotal = acquisition.analytics.STR
      .assetManagementFeeTotalSTR
      ? acquisition.analytics.STR.assetManagementFeeTotalSTR
      : 0

    const homeInsurance = acquisition.analytics.STR
      .propertyInsuranceDollarSTR
      ? acquisition.analytics.STR.propertyInsuranceDollarSTR
      : 0
    const maintenanceDollar = acquisition.analytics.STR
      .maintenanceSTRDollar
      ? acquisition.analytics.STR.maintenanceSTRDollar
      : 0
    const monthlyReserves = acquisition.analytics.STR
      .monthlyReservesTypeDollarSTR
      ? acquisition.analytics.STR.monthlyReservesTypeDollarSTR
      : 0

    const rentAmount =
      assumptions.rentSTRType === 'rentRange'
        ? acquisition.analysis.rentLowSTR &&
          acquisition.analysis.rentHighSTR
          ? map(
              zip(
                acquisition.analysis.rentLowSTR,
                acquisition.analysis.rentHighSTR,
              ),
              ([low, high]) => (low + high) / 2,
            )
          : [0]
        : acquisition.analysis.rentSTR
          ? acquisition.analysis.rentSTR
          : [0]
    const otherIncome = acquisition.analysis.otherIncome
      ? acquisition.analysis.otherIncome
      : [0]
    const propertyTaxes = acquisition.analysis.propertyTaxes
      ? acquisition.analysis.propertyTaxes
      : 0
    const monthlyHOA = acquisition.analytics.STR.hoaTotal
      ? acquisition.analytics.STR.hoaTotal / 12
      : 0
    const offerPrice = acquisition.analysis.offerPrice
      ? acquisition.analysis.offerPrice
      : acquisition.analysis.listPrice
    const financingRepairsPercentage = acquisition.analysis
      .financingRepairsPercentageSTR
      ? acquisition.analysis.financingRepairsPercentageSTR
      : 0
    const rehabCost = acquisition.analytics.STR.corTotalSTR
      ? acquisition.analytics.STR.corTotalSTR
      : 0
    const vacancy = assumptions.vacancy ? assumptions.vacancy : 0
    const maintenance = assumptions.maintenanceSTR
      ? assumptions.maintenanceSTR
      : 0
    const cohostingPercentage = acquisition.analytics.STR
      .cohostingPercentage
      ? acquisition.analytics.STR.cohostingPercentage
      : 0
    const annualRevenueIncrease = assumptions.growthRateIncome
      ? assumptions.growthRateIncome
      : 0
    const annualOperatingExpenseIncrease =
      assumptions.growthRateExpense
        ? assumptions.growthRateExpense
        : 0
    const acquisitionFee = assumptions.acquisitionFee
      ? assumptions.acquisitionFee
      : 0
    const assetManagementFee = assumptions.assetManagementFee
      ? assumptions.assetManagementFee
      : 0

    const otherExpenses = assumptions.otherExpenseSTR
      ? assumptions.otherExpenseSTR
      : 0

    const ownerReserves = assumptions.ownerReservesSTR
      ? assumptions.ownerReservesSTR
      : 0

    const customAcquisitionExpenseTotal = acquisition.analytics
      .customAcquisitionExpenseTotal
      ? acquisition.analytics.customAcquisitionExpenseTotal
      : 0
    const customAnnualExpenseTotal = acquisition.analytics
      .customMonthlyExpenseTotal
      ? acquisition.analytics.customMonthlyExpenseTotal * 12
      : 0

    const occupancyRate = assumptions.occupancyRate
      ? assumptions.occupancyRate
      : 0

    const renovationAmount = rehabCost || 0

    const totalInitialInvestmentFinancing =
      downPaymentTotal +
      acquisitionCommissionDollar +
      acquisitionFee +
      closeCostDollar +
      financingFeeTotal +
      pointsAmount -
      creditFromSeller +
      renovationAmount * (1 - financingRepairsPercentage / 100) +
      customAcquisitionExpenseTotal
    const totalInitialInvestmentNoFinancing =
      offerPrice +
      renovationAmount +
      acquisitionCommissionDollar +
      acquisitionFee +
      closeCostDollar +
      pointsAmount +
      financingFeeTotal +
      ownerReserves +
      customAcquisitionExpenseTotal
    const yearlyHOA = monthlyHOA * 12
    const rentalIncome3 = revenuesSection({
      revenueYear: 3,
      rentAmount: rentAmount,
      annualRevenueIncrease: annualRevenueIncrease / 100,
    })
    const rentalIncome5 = revenuesSection({
      revenueYear: 5,
      rentAmount: rentAmount,
      annualRevenueIncrease: annualRevenueIncrease / 100,
    })
    const rentalIncome10 = revenuesSection({
      revenueYear: 10,
      rentAmount: rentAmount,
      annualRevenueIncrease: annualRevenueIncrease / 100,
    })

    const avgMonthlyRate = map(
      avgRate,
      (avgRate) => (avgRate * 365) / 12,
    )

    const potentialRentalIncome3 = revenuesSectionSTR({
      revenueYear: 3,
      avgMonthlyRate: avgMonthlyRate,
      annualRevenueIncrease: annualRevenueIncrease / 100,
    })
    const potentialRentalIncome5 = revenuesSectionSTR({
      revenueYear: 5,
      avgMonthlyRate: avgMonthlyRate,
      annualRevenueIncrease: annualRevenueIncrease / 100,
    })
    const potentialRentalIncome10 = revenuesSectionSTR({
      revenueYear: 10,
      avgMonthlyRate: avgMonthlyRate,
      annualRevenueIncrease: annualRevenueIncrease / 100,
    })

    const occupancyRateMonthly = sum(
      map(
        zip(avgMonthlyRate, occupancyRate),
        ([avgMonthlyRate, occupancyRate]) =>
          (avgMonthlyRate * occupancyRate) / 100,
      ),
    )
    const occupancyRate1 = occupancyRateMonthly * 12
    const occupancyRate3 = sum(
      map(
        zip(potentialRentalIncome3, occupancyRate),
        ([avgRate, occupancyRate]) => (avgRate * occupancyRate) / 100,
      ),
    )
    const occupancyRate5 = sum(
      map(
        zip(potentialRentalIncome5, occupancyRate),
        ([avgRate, occupancyRate]) => (avgRate * occupancyRate) / 100,
      ),
    )
    const occupancyRate10 = sum(
      map(
        zip(potentialRentalIncome10, occupancyRate),
        ([avgRate, occupancyRate]) => (avgRate * occupancyRate) / 100,
      ),
    )

    const vacancyRateMonthly = sum(
      map(
        zip(rentAmount, vacancy),
        ([rentAmount, vacancy]) => rentAmount * (vacancy / 100),
      ),
    )
    const vacancyRate1 =
      sum(
        map(
          zip(rentAmount, vacancy),
          ([rentAmount, vacancy]) => rentAmount * (vacancy / 100),
        ),
      ) * 12
    const vacancyRate3 = sum(
      map(
        zip(rentalIncome3, vacancy),
        ([rentalIncome3, vacancy]) => rentalIncome3 * (vacancy / 100),
      ),
    )
    const vacancyRate5 = sum(
      map(
        zip(rentalIncome5, vacancy),
        ([rentalIncome5, vacancy]) => rentalIncome5 * (vacancy / 100),
      ),
    )
    const vacancyRate10 = sum(
      map(
        zip(rentalIncome10, vacancy),
        ([rentalIncome10, vacancy]) =>
          rentalIncome10 * (vacancy / 100),
      ),
    )
    const otherIncomeMonthly = sum(otherIncome)
    const otherIncome1 = sum(otherIncome) * 12
    const otherIncome3 = revenuesSection({
      revenueYear: 3,
      rentAmount: otherIncome,
      annualRevenueIncrease: annualRevenueIncrease / 100,
    })
    const otherIncome5 = revenuesSection({
      revenueYear: 5,
      rentAmount: otherIncome,
      annualRevenueIncrease: annualRevenueIncrease / 100,
    })
    const otherIncome10 = revenuesSection({
      revenueYear: 10,
      rentAmount: otherIncome,
      annualRevenueIncrease: annualRevenueIncrease / 100,
    })
    const grossRentalIncome1 =
      acquisition.analytics.STR.grossAnnualRentSTR
    const grossRentalIncomeMonthly = map(
      grossRentalIncome1,
      (grossRentalIncome1) => grossRentalIncome1 / 12,
    )
    const grossRentalIncome3 = revenuesSectionSTR({
      revenueYear: 3,
      avgMonthlyRate: grossRentalIncomeMonthly,
      annualRevenueIncrease: annualRevenueIncrease / 100,
    })
    const grossRentalIncome5 = revenuesSectionSTR({
      revenueYear: 5,
      avgMonthlyRate: grossRentalIncomeMonthly,
      annualRevenueIncrease: annualRevenueIncrease / 100,
    })
    const grossRentalIncome10 = revenuesSectionSTR({
      revenueYear: 10,
      avgMonthlyRate: grossRentalIncomeMonthly,
      annualRevenueIncrease: annualRevenueIncrease / 100,
    })
    const monthlyRevenue =
      sum(grossRentalIncomeMonthly) + otherIncomeMonthly
    const revenueTotal1 = sum(grossRentalIncome1) + otherIncome1
    const revenueTotal3 = sum(grossRentalIncome3) + sum(otherIncome3)
    const revenueTotal5 = sum(grossRentalIncome5) + sum(otherIncome5)
    const revenueTotal10 =
      sum(grossRentalIncome10) + sum(otherIncome10)

    const propertyTaxes3 = expensesSection({
      expenseType: 'PT',
      revenueYear: 3,
      propertyTaxes,
      annualOperatingExpenseIncrease:
        annualOperatingExpenseIncrease / 100,
    })
    const propertyTaxes5 = expensesSection({
      expenseType: 'PT',
      revenueYear: 5,
      propertyTaxes,
      annualOperatingExpenseIncrease:
        annualOperatingExpenseIncrease / 100,
    })
    const propertyTaxes10 = expensesSection({
      expenseType: 'PT',
      revenueYear: 10,
      propertyTaxes,
      annualOperatingExpenseIncrease:
        annualOperatingExpenseIncrease / 100,
    })
    const homeInsurance3 = expensesSection({
      expenseType: 'INSURANCE',
      revenueYear: 3,
      homeInsurance,
      annualOperatingExpenseIncrease:
        annualOperatingExpenseIncrease / 100,
    })
    const homeInsurance5 = expensesSection({
      expenseType: 'INSURANCE',
      revenueYear: 5,
      homeInsurance,
      annualOperatingExpenseIncrease:
        annualOperatingExpenseIncrease / 100,
    })
    const homeInsurance10 = expensesSection({
      expenseType: 'INSURANCE',
      revenueYear: 10,
      homeInsurance,
      annualOperatingExpenseIncrease:
        annualOperatingExpenseIncrease / 100,
    })
    const HOA3 = expensesSection({
      expenseType: 'HOA',
      revenueYear: 3,
      yearlyHOA,
      annualOperatingExpenseIncrease:
        annualOperatingExpenseIncrease / 100,
    })
    const HOA5 = expensesSection({
      expenseType: 'HOA',
      revenueYear: 5,
      yearlyHOA,
      annualOperatingExpenseIncrease:
        annualOperatingExpenseIncrease / 100,
    })
    const HOA10 = expensesSection({
      expenseType: 'HOA',
      revenueYear: 10,
      yearlyHOA,
      annualOperatingExpenseIncrease:
        annualOperatingExpenseIncrease / 100,
    })
    const cohosting1 = acquisition.analytics.STR.cohostingDollar
    const cohosting3 =
      assumptions.cohostingType === '$'
        ? cohostingDollar
        : sum(grossRentalIncome3) * (cohostingPercentage / 100)
    const cohosting5 =
      assumptions.cohostingType === '$'
        ? cohostingDollar
        : sum(grossRentalIncome5) * (cohostingPercentage / 100)
    const cohosting10 =
      assumptions.cohostingType === '$'
        ? cohostingDollar
        : sum(grossRentalIncome10) * (cohostingPercentage / 100)
    const reserves1 = monthlyReserves * 12
    const reserves3 = expensesSection({
      expenseType: 'RESERVE',
      revenueYear: 3,
      rentAmount: sum(grossRentalIncome1),
      yearlyReserve: reserves1,
      annualOperatingExpenseIncrease:
        annualOperatingExpenseIncrease / 100,
    })
    const reserves5 = expensesSection({
      expenseType: 'RESERVE',
      revenueYear: 5,
      rentAmount: sum(grossRentalIncome1),
      yearlyReserve: reserves1,
      annualOperatingExpenseIncrease:
        annualOperatingExpenseIncrease / 100,
    })
    const reserves10 = expensesSection({
      expenseType: 'RESERVE',
      revenueYear: 10,
      rentAmount: sum(grossRentalIncome1),
      yearlyReserve: reserves1,
      annualOperatingExpenseIncrease:
        annualOperatingExpenseIncrease / 100,
    })
    const maintenance1 = maintenanceDollar
    const maintenance3 = expensesSection({
      expenseType: 'MAINTENANCE',
      revenueYear: 3,
      rentAmount: sum(grossRentalIncome1),
      maintenance: maintenance / 100,
      annualOperatingExpenseIncrease:
        annualOperatingExpenseIncrease / 100,
    })
    const maintenance5 = expensesSection({
      expenseType: 'MAINTENANCE',
      revenueYear: 5,
      rentAmount: sum(grossRentalIncome1),
      maintenance: maintenance / 100,
      annualOperatingExpenseIncrease:
        annualOperatingExpenseIncrease / 100,
    })
    const maintenance10 = expensesSection({
      expenseType: 'MAINTENANCE',
      revenueYear: 10,
      rentAmount: sum(grossRentalIncome1),
      maintenance: maintenance / 100,
      annualOperatingExpenseIncrease:
        annualOperatingExpenseIncrease / 100,
    })
    const utilities1 = utilities * 12
    const utilities3 =
      assumptions.utilitiesSTRType === '$'
        ? utilities * 12
        : sum(grossRentalIncome3) * (utilitiesPercent / 100)
    const utilities5 =
      assumptions.utilitiesSTRType === '$'
        ? utilities * 12
        : sum(grossRentalIncome5) * (utilitiesPercent / 100)
    const utilities10 =
      assumptions.utilitiesSTRType === '$'
        ? utilities * 12
        : sum(grossRentalIncome10) * (utilitiesPercent / 100)
    const assetFee1 = assetManagementFeeTotal
    const assetFee3 = expensesSection({
      expenseType: 'ASSET',
      revenueYear: 3,
      rentAmount: grossRentalIncome1,
      assetManagementFeeTotal: assetManagementFeeTotal,
      totalInitialInvestmentFinancing,
    })
    const assetFee5 = expensesSection({
      expenseType: 'ASSET',
      revenueYear: 5,
      rentAmount: grossRentalIncome1,
      assetManagementFeeTotal: assetManagementFeeTotal,
      totalInitialInvestmentFinancing,
    })
    const assetFee10 = expensesSection({
      expenseType: 'ASSET',
      revenueYear: 10,
      rentAmount: grossRentalIncome1,
      assetManagementFeeTotal: assetManagementFeeTotal,
      totalInitialInvestmentFinancing,
    })
    const otherExpense3 = expensesSection({
      expenseType: 'OTHER',
      revenueYear: 3,
      rentAmount: grossRentalIncome1,
      otherExpenses,
      annualOperatingExpenseIncrease:
        annualOperatingExpenseIncrease / 100,
    })
    const otherExpense5 = expensesSection({
      expenseType: 'OTHER',
      revenueYear: 5,
      rentAmount: grossRentalIncome1,
      otherExpenses,
      annualOperatingExpenseIncrease:
        annualOperatingExpenseIncrease / 100,
    })
    const otherExpense10 = expensesSection({
      expenseType: 'OTHER',
      revenueYear: 10,
      rentAmount: grossRentalIncome1,
      otherExpenses,
      annualOperatingExpenseIncrease:
        annualOperatingExpenseIncrease / 100,
    })
    const customAnnualExpenseTotal3 = expensesSection({
      expenseType: 'CUSTOM',
      revenueYear: 3,
      rentAmount: grossRentalIncome1,
      customAnnualExpenseTotal,
      annualOperatingExpenseIncrease:
        annualOperatingExpenseIncrease / 100,
    })
    const customAnnualExpenseTotal5 = expensesSection({
      expenseType: 'CUSTOM',
      revenueYear: 5,
      rentAmount: grossRentalIncome1,
      customAnnualExpenseTotal,
      annualOperatingExpenseIncrease:
        annualOperatingExpenseIncrease / 100,
    })
    const customAnnualExpenseTotal10 = expensesSection({
      expenseType: 'CUSTOM',
      revenueYear: 10,
      rentAmount: grossRentalIncome1,
      customAnnualExpenseTotal,
      annualOperatingExpenseIncrease:
        annualOperatingExpenseIncrease / 100,
    })
    const totalExpenseMonthly =
      propertyTaxes / 12 +
      homeInsurance / 12 +
      monthlyHOA +
      cohostingDollar +
      (maintenance / 100) * sum(grossRentalIncomeMonthly) +
      utilities +
      monthlyReserves +
      assetManagementFeeTotal / 12 +
      otherExpenses / 12 +
      customAnnualExpenseTotal / 12
    const totalExpense1 =
      propertyTaxes +
      homeInsurance +
      yearlyHOA +
      cohosting1 +
      maintenance1 +
      utilities1 +
      reserves1 +
      assetFee1 +
      otherExpenses +
      customAnnualExpenseTotal
    const totalExpense3 =
      propertyTaxes3 +
      homeInsurance3 +
      HOA3 +
      cohosting3 +
      maintenance3 +
      utilities3 +
      reserves3 +
      assetFee3 +
      otherExpense3 +
      customAnnualExpenseTotal3
    const totalExpense5 =
      propertyTaxes5 +
      homeInsurance5 +
      HOA5 +
      cohosting5 +
      maintenance5 +
      utilities5 +
      reserves5 +
      assetFee5 +
      otherExpense5 +
      customAnnualExpenseTotal5
    const totalExpense10 =
      propertyTaxes10 +
      homeInsurance10 +
      HOA10 +
      cohosting10 +
      maintenance10 +
      reserves10 +
      utilities10 +
      assetFee10 +
      otherExpense10 +
      customAnnualExpenseTotal10
    const expenseRatio1 = (totalExpense1 / grossRentalIncome1) * 100
    const expenseRatio3 = (totalExpense3 / grossRentalIncome3) * 100
    const expenseRatio5 = (totalExpense5 / grossRentalIncome5) * 100
    const expenseRatio10 =
      (totalExpense10 / grossRentalIncome10) * 100
    const NOINoFinanceMonthly = monthlyRevenue - totalExpenseMonthly
    const NOINoFinance1 = revenueTotal1 - totalExpense1
    const NOINoFinance3 = revenueTotal3 - totalExpense3
    const NOINoFinance5 = revenueTotal5 - totalExpense5
    const NOINoFinance10 = revenueTotal10 - totalExpense10
    const NOIFinanceMonthly = monthlyRevenue - totalExpenseMonthly
    const NOIFinance1 = revenueTotal1 - totalExpense1
    const NOIFinance3 = revenueTotal3 - totalExpense3
    const NOIFinance5 = revenueTotal5 - totalExpense5
    const NOIFinance10 = revenueTotal10 - totalExpense10
    const mortgagePaymentYearly = mortgagePayment * 12
    const totalReturnFinancing = []
    const totalReturnFinancing1 =
      NOINoFinance1 - mortgagePaymentYearly
    const totalReturnFinancing3 =
      NOINoFinance3 - mortgagePaymentYearly
    const totalReturnFinancing5 =
      NOINoFinance5 - mortgagePaymentYearly
    const totalReturnFinancing10 =
      NOINoFinance10 - mortgagePaymentYearly
    totalReturnFinancing.push({
      one: totalReturnFinancing1,
      three: totalReturnFinancing3,
      five: totalReturnFinancing5,
      ten: totalReturnFinancing10,
    })
    const totalROIFinancing1 =
      (totalReturnFinancing1 / totalInitialInvestmentFinancing) * 100
    const totalROIFinancing3 =
      (totalReturnFinancing3 / totalInitialInvestmentFinancing) * 100
    const totalROIFinancing5 =
      (totalReturnFinancing5 / totalInitialInvestmentFinancing) * 100
    const totalROIFinancing10 =
      (totalReturnFinancing10 / totalInitialInvestmentFinancing) * 100
    const totalReturnNoFinancing1 = NOINoFinance1
    const totalReturnNoFinancing3 = NOINoFinance3
    const totalReturnNoFinancing5 = NOINoFinance5
    const totalReturnNoFinancing10 = NOINoFinance10
    const totalROINoFinancing1 =
      (totalReturnNoFinancing1 / totalInitialInvestmentNoFinancing) *
      100
    const totalROINoFinancing3 =
      (totalReturnNoFinancing3 / totalInitialInvestmentNoFinancing) *
      100
    const totalROINoFinancing5 =
      (totalReturnNoFinancing5 / totalInitialInvestmentNoFinancing) *
      100
    const totalROINoFinancing10 =
      (totalReturnNoFinancing10 / totalInitialInvestmentNoFinancing) *
      100

    return [
      {
        margin: [15, 10, 15, 0],
        table: {
          headerRows: 0,
          widths: [150, '*', '*', '*', '*', '*'],
          body: [
            [
              {
                text: 'Annual Revenue Increase',
                alignment: 'left',
                fontSize: 6,
              },
              { text: '' },
              {
                text: annualRevenueIncrease + '%',
                alignment: 'right',
                fontSize: 6,
              },
              {
                text: annualRevenueIncrease + '%',
                alignment: 'right',
                fontSize: 6,
              },
              {
                text: annualRevenueIncrease + '%',
                alignment: 'right',
                fontSize: 6,
              },
              {
                text: annualRevenueIncrease + '%',
                alignment: 'right',
                fontSize: 6,
              },
            ],
            [
              {
                text: 'Annual Operating Expense Increase',
                alignment: 'left',
                fontSize: 6,
              },
              { text: '' },
              {
                text: annualOperatingExpenseIncrease + '%',
                alignment: 'right',
                fontSize: 6,
              },
              {
                text: annualOperatingExpenseIncrease + '%',
                alignment: 'right',
                fontSize: 6,
              },
              {
                text: annualOperatingExpenseIncrease + '%',
                alignment: 'right',
                fontSize: 6,
              },
              {
                text: annualOperatingExpenseIncrease + '%',
                alignment: 'right',
                fontSize: 6,
              },
            ],
            [
              {
                text: 'Annual Expense Ratio',
                alignment: 'left',
                fontSize: 6,
              },
              { text: '' },
              {
                text: expenseRatio1.toFixed(2) + '%',
                alignment: 'right',
                fontSize: 6,
              },
              {
                text: expenseRatio3.toFixed(2) + '%',
                alignment: 'right',
                fontSize: 6,
              },
              {
                text: expenseRatio5.toFixed(2) + '%',
                alignment: 'right',
                fontSize: 6,
              },
              {
                text: expenseRatio10.toFixed(2) + '%',
                alignment: 'right',
                fontSize: 6,
              },
            ],
            //[{text: 'Estimated Tax Bracket', alignment: 'left',fontSize: 6,}, {text: '$123.00', alignment: 'right',fontSize: 6,},{text: '0%', alignment: 'right',fontSize: 6,}, {text: '1%', alignment: 'right',fontSize: 6,}, {text: '1%', alignment: 'right',fontSize: 6,}, {text: '1%', alignment: 'right',fontSize: 6,}]
          ],
        },
        layout: {
          defaultBorder: false,
        },
      },
      {
        margin: [15, 5, 15, 0],
        table: {
          headerRows: 0,
          widths: [150, '*', '*', '*', '*', '*'],
          body: [
            [
              { text: '', alignment: 'left', fontSize: 6 },
              {
                text: 'Monthly',
                alignment: 'right',
                fontSize: 6,
                bold: true,
              },
              {
                text: 'Year 1',
                alignment: 'right',
                fontSize: 6,
                bold: true,
              },
              {
                text: 'Year 3',
                alignment: 'right',
                fontSize: 6,
                bold: true,
              },
              {
                text: 'Year 5',
                alignment: 'right',
                fontSize: 6,
                bold: true,
              },
              {
                text: 'Year 10',
                alignment: 'right',
                fontSize: 6,
                bold: true,
              },
            ],
          ],
        },
        layout: {
          defaultBorder: false,
        },
      },
      {
        margin: [15, 0, 15, 0],
        table: {
          headerRows: 1,
          widths: [150, '*', '*', '*', '*', '*'],
          body: compact([
            [
              {
                text: 'Revenues',
                fillColor: '#3399ff',
                color: 'white',
                fontSize: 8,
                colSpan: 6,
                alignment: 'left',
              },
              { text: '' },
              { text: '' },
              { text: '' },
              { text: '' },
              { text: '' },
            ],
            avgMonthlyRate
              ? [
                  {
                    text: 'Potential Rental Income',
                    alignment: 'left',
                    fontSize: 6,
                  },
                  {
                    text: currencyFormat.format(sum(avgMonthlyRate)),
                    alignment: 'right',
                    fontSize: 6,
                  },
                  {
                    text: currencyFormat.format(
                      sum(avgMonthlyRate) * 12,
                    ),
                    alignment: 'right',
                    fontSize: 6,
                  },
                  {
                    text: currencyFormat.format(
                      sum(potentialRentalIncome3),
                    ),
                    alignment: 'right',
                    fontSize: 6,
                  },
                  {
                    text: currencyFormat.format(
                      sum(potentialRentalIncome5),
                    ),
                    alignment: 'right',
                    fontSize: 6,
                  },
                  {
                    text: currencyFormat.format(
                      sum(potentialRentalIncome10),
                    ),
                    alignment: 'right',
                    fontSize: 6,
                  },
                ]
              : null,
            occupancyRate1
              ? [
                  {
                    text: 'Occupancy Rate',
                    alignment: 'left',
                    fontSize: 6,
                  },
                  {
                    text: currencyFormat.format(occupancyRateMonthly),
                    alignment: 'right',
                    fontSize: 6,
                  },
                  {
                    text: currencyFormat.format(occupancyRate1),
                    alignment: 'right',
                    fontSize: 6,
                  },
                  {
                    text: currencyFormat.format(occupancyRate3),
                    alignment: 'right',
                    fontSize: 6,
                  },
                  {
                    text: currencyFormat.format(occupancyRate5),
                    alignment: 'right',
                    fontSize: 6,
                  },
                  {
                    text: currencyFormat.format(occupancyRate10),
                    alignment: 'right',
                    fontSize: 6,
                  },
                ]
              : null,
            otherIncomeMonthly
              ? [
                  {
                    text: 'Other Income',
                    alignment: 'left',
                    fontSize: 6,
                  },
                  {
                    text: currencyFormat.format(otherIncomeMonthly),
                    alignment: 'right',
                    fontSize: 6,
                  },
                  {
                    text: currencyFormat.format(otherIncome1),
                    alignment: 'right',
                    fontSize: 6,
                  },
                  {
                    text: currencyFormat.format(sum(otherIncome3)),
                    alignment: 'right',
                    fontSize: 6,
                  },
                  {
                    text: currencyFormat.format(sum(otherIncome5)),
                    alignment: 'right',
                    fontSize: 6,
                  },
                  {
                    text: currencyFormat.format(sum(otherIncome10)),
                    alignment: 'right',
                    fontSize: 6,
                  },
                ]
              : null,
            [
              {
                text: 'Gross Rental Income',
                alignment: 'left',
                fontSize: 6,
                bold: true,
                border: [false, true, false, false],
              },
              {
                text: currencyFormat.format(monthlyRevenue),
                alignment: 'right',
                fontSize: 6,
                bold: true,
                border: [false, true, false, false],
              },
              {
                text: currencyFormat.format(revenueTotal1),
                alignment: 'right',
                fontSize: 6,
                bold: true,
                border: [false, true, false, false],
              },
              {
                text: currencyFormat.format(revenueTotal3),
                alignment: 'right',
                fontSize: 6,
                bold: true,
                border: [false, true, false, false],
              },
              {
                text: currencyFormat.format(revenueTotal5),
                alignment: 'right',
                fontSize: 6,
                bold: true,
                border: [false, true, false, false],
              },
              {
                text: currencyFormat.format(revenueTotal10),
                alignment: 'right',
                fontSize: 6,
                bold: true,
                border: [false, true, false, false],
              },
            ],
          ]),
        },
        layout: {
          defaultBorder: false,
        },
      },
      {
        margin: [15, 5, 15, 0],
        table: {
          headerRows: 1,
          widths: [150, '*', '*', '*', '*', '*'],
          body: compact([
            [
              {
                text: 'Expenses',
                fillColor: '#3399ff',
                color: 'white',
                fontSize: 8,
                colSpan: 6,
                alignment: 'left',
              },
              { text: '' },
              { text: '' },
              { text: '' },
              { text: '' },
              { text: '' },
            ],
            propertyTaxes
              ? [
                  {
                    text: 'Estimated Annual Property Taxes',
                    alignment: 'left',
                    fontSize: 6,
                  },
                  {
                    text: currencyFormat.format(propertyTaxes / 12),
                    alignment: 'right',
                    fontSize: 6,
                  },
                  {
                    text: currencyFormat.format(propertyTaxes),
                    alignment: 'right',
                    fontSize: 6,
                  },
                  {
                    text: currencyFormat.format(propertyTaxes3),
                    alignment: 'right',
                    fontSize: 6,
                  },
                  {
                    text: currencyFormat.format(propertyTaxes5),
                    alignment: 'right',
                    fontSize: 6,
                  },
                  {
                    text: currencyFormat.format(propertyTaxes10),
                    alignment: 'right',
                    fontSize: 6,
                  },
                ]
              : null,
            homeInsurance
              ? [
                  {
                    text: 'Estimated Annual Homeowners Insurance',
                    alignment: 'left',
                    fontSize: 6,
                  },
                  {
                    text: currencyFormat.format(homeInsurance / 12),
                    alignment: 'right',
                    fontSize: 6,
                  },
                  {
                    text: currencyFormat.format(homeInsurance),
                    alignment: 'right',
                    fontSize: 6,
                  },
                  {
                    text: currencyFormat.format(homeInsurance3),
                    alignment: 'right',
                    fontSize: 6,
                  },
                  {
                    text: currencyFormat.format(homeInsurance5),
                    alignment: 'right',
                    fontSize: 6,
                  },
                  {
                    text: currencyFormat.format(homeInsurance10),
                    alignment: 'right',
                    fontSize: 6,
                  },
                ]
              : null,
            monthlyHOA
              ? [
                  {
                    text: 'Estimated Annual HOA Dues',
                    alignment: 'left',
                    fontSize: 6,
                  },
                  {
                    text: currencyFormat.format(monthlyHOA),
                    alignment: 'right',
                    fontSize: 6,
                  },
                  {
                    text: currencyFormat.format(yearlyHOA),
                    alignment: 'right',
                    fontSize: 6,
                  },
                  {
                    text: currencyFormat.format(HOA3),
                    alignment: 'right',
                    fontSize: 6,
                  },
                  {
                    text: currencyFormat.format(HOA5),
                    alignment: 'right',
                    fontSize: 6,
                  },
                  {
                    text: currencyFormat.format(HOA10),
                    alignment: 'right',
                    fontSize: 6,
                  },
                ]
              : null,
            cohostingDollar
              ? [
                  {
                    text:
                      'Cohosting Fee (' + cohostingPercentage + '%)',
                    alignment: 'left',
                    fontSize: 6,
                  },
                  {
                    text: currencyFormat.format(cohostingDollar),
                    alignment: 'right',
                    fontSize: 6,
                  },
                  {
                    text: currencyFormat.format(cohosting1),
                    alignment: 'right',
                    fontSize: 6,
                  },
                  {
                    text: currencyFormat.format(cohosting3),
                    alignment: 'right',
                    fontSize: 6,
                  },
                  {
                    text: currencyFormat.format(cohosting5),
                    alignment: 'right',
                    fontSize: 6,
                  },
                  {
                    text: currencyFormat.format(cohosting10),
                    alignment: 'right',
                    fontSize: 6,
                  },
                ]
              : null,
            reserves1
              ? [
                  {
                    text: 'Estimated Annual Reserves',
                    alignment: 'left',
                    fontSize: 6,
                  },
                  {
                    text: currencyFormat.format(monthlyReserves),
                    alignment: 'right',
                    fontSize: 6,
                  },
                  {
                    text: currencyFormat.format(reserves1),
                    alignment: 'right',
                    fontSize: 6,
                  },
                  {
                    text: currencyFormat.format(reserves3),
                    alignment: 'right',
                    fontSize: 6,
                  },
                  {
                    text: currencyFormat.format(reserves5),
                    alignment: 'right',
                    fontSize: 6,
                  },
                  {
                    text: currencyFormat.format(reserves10),
                    alignment: 'right',
                    fontSize: 6,
                  },
                ]
              : null,
            maintenance1
              ? [
                  {
                    text:
                      'Maintenance & Repairs (' + maintenance + '%)',
                    alignment: 'left',
                    fontSize: 6,
                  },
                  {
                    text: currencyFormat.format(
                      (maintenance / 100) *
                        sum(grossRentalIncomeMonthly),
                    ),
                    alignment: 'right',
                    fontSize: 6,
                  },
                  {
                    text: currencyFormat.format(maintenance1),
                    alignment: 'right',
                    fontSize: 6,
                  },
                  {
                    text: currencyFormat.format(maintenance3),
                    alignment: 'right',
                    fontSize: 6,
                  },
                  {
                    text: currencyFormat.format(maintenance5),
                    alignment: 'right',
                    fontSize: 6,
                  },
                  {
                    text: currencyFormat.format(maintenance10),
                    alignment: 'right',
                    fontSize: 6,
                  },
                ]
              : null,
            utilities
              ? [
                  {
                    text: 'Utilities',
                    alignment: 'left',
                    fontSize: 6,
                  },
                  {
                    text: currencyFormat.format(utilities),
                    alignment: 'right',
                    fontSize: 6,
                  },
                  {
                    text: currencyFormat.format(utilities1),
                    alignment: 'right',
                    fontSize: 6,
                  },
                  {
                    text: currencyFormat.format(utilities3),
                    alignment: 'right',
                    fontSize: 6,
                  },
                  {
                    text: currencyFormat.format(utilities5),
                    alignment: 'right',
                    fontSize: 6,
                  },
                  {
                    text: currencyFormat.format(utilities10),
                    alignment: 'right',
                    fontSize: 6,
                  },
                ]
              : null,
            assetFee1
              ? [
                  {
                    text:
                      'Asset Management Fee (' +
                      assetManagementFee +
                      '%)',
                    alignment: 'left',
                    fontSize: 6,
                  },
                  {
                    text: currencyFormat.format(assetFee1 / 12),
                    alignment: 'right',
                    fontSize: 6,
                  },
                  {
                    text: currencyFormat.format(assetFee1),
                    alignment: 'right',
                    fontSize: 6,
                  },
                  {
                    text: currencyFormat.format(assetFee3),
                    alignment: 'right',
                    fontSize: 6,
                  },
                  {
                    text: currencyFormat.format(assetFee5),
                    alignment: 'right',
                    fontSize: 6,
                  },
                  {
                    text: currencyFormat.format(assetFee10),
                    alignment: 'right',
                    fontSize: 6,
                  },
                ]
              : null,
            otherExpenses
              ? [
                  {
                    text: 'Other Expenses',
                    alignment: 'left',
                    fontSize: 6,
                  },
                  {
                    text: currencyFormat.format(otherExpenses / 12),
                    alignment: 'right',
                    fontSize: 6,
                  },
                  {
                    text: currencyFormat.format(otherExpenses),
                    alignment: 'right',
                    fontSize: 6,
                  },
                  {
                    text: currencyFormat.format(otherExpense3),
                    alignment: 'right',
                    fontSize: 6,
                  },
                  {
                    text: currencyFormat.format(otherExpense5),
                    alignment: 'right',
                    fontSize: 6,
                  },
                  {
                    text: currencyFormat.format(otherExpense10),
                    alignment: 'right',
                    fontSize: 6,
                  },
                ]
              : null,
            customAnnualExpenseTotal
              ? [
                  {
                    text: 'Custom Expenses',
                    alignment: 'left',
                    fontSize: 6,
                  },
                  {
                    text: currencyFormat.format(
                      customAnnualExpenseTotal / 12,
                    ),
                    alignment: 'right',
                    fontSize: 6,
                  },
                  {
                    text: currencyFormat.format(
                      customAnnualExpenseTotal,
                    ),
                    alignment: 'right',
                    fontSize: 6,
                  },
                  {
                    text: currencyFormat.format(
                      customAnnualExpenseTotal3,
                    ),
                    alignment: 'right',
                    fontSize: 6,
                  },
                  {
                    text: currencyFormat.format(
                      customAnnualExpenseTotal5,
                    ),
                    alignment: 'right',
                    fontSize: 6,
                  },
                  {
                    text: currencyFormat.format(
                      customAnnualExpenseTotal10,
                    ),
                    alignment: 'right',
                    fontSize: 6,
                  },
                ]
              : null,
            [
              {
                text: 'Total Expenses',
                alignment: 'left',
                fontSize: 6,
                bold: true,
                border: [false, true, false, false],
              },
              {
                text: currencyFormat.format(totalExpenseMonthly),
                alignment: 'right',
                fontSize: 6,
                bold: true,
                border: [false, true, false, false],
              },
              {
                text: currencyFormat.format(totalExpense1),
                alignment: 'right',
                fontSize: 6,
                bold: true,
                border: [false, true, false, false],
              },
              {
                text: currencyFormat.format(totalExpense3),
                alignment: 'right',
                fontSize: 6,
                bold: true,
                border: [false, true, false, false],
              },
              {
                text: currencyFormat.format(totalExpense5),
                alignment: 'right',
                fontSize: 6,
                bold: true,
                border: [false, true, false, false],
              },
              {
                text: currencyFormat.format(totalExpense10),
                alignment: 'right',
                fontSize: 6,
                bold: true,
                border: [false, true, false, false],
              },
            ],
          ]),
        },
        layout: {
          defaultBorder: false,
        },
      },
      {
        margin: [15, 5, 15, 0],
        table: {
          headerRows: 1,
          widths: [150, '*', '*', '*', '*', '*'],
          body: [
            [
              {
                text: 'Returns Without Financing',
                fillColor: '#3399ff',
                color: 'white',
                fontSize: 8,
                colSpan: 6,
                alignment: 'left',
              },
              { text: '' },
              { text: '' },
              { text: '' },
              { text: '' },
              { text: '' },
            ],
            [
              {
                text: 'NOI (Cash Available)',
                alignment: 'left',
                fontSize: 6,
              },
              {
                text: currencyFormat.format(NOINoFinanceMonthly),
                alignment: 'right',
                fontSize: 6,
              },
              {
                text: currencyFormat.format(NOINoFinance1),
                alignment: 'right',
                fontSize: 6,
              },
              {
                text: currencyFormat.format(NOINoFinance3),
                alignment: 'right',
                fontSize: 6,
              },
              {
                text: currencyFormat.format(NOINoFinance5),
                alignment: 'right',
                fontSize: 6,
              },
              {
                text: currencyFormat.format(NOINoFinance10),
                alignment: 'right',
                fontSize: 6,
              },
            ],
            [
              {
                text: 'Net Cashflow',
                alignment: 'left',
                fontSize: 6,
                bold: true,
              },
              {
                text: '',
                alignment: 'right',
                fontSize: 6,
                bold: true,
              },
              {
                text: currencyFormat.format(totalReturnNoFinancing1),
                alignment: 'right',
                fontSize: 6,
                bold: true,
              },
              {
                text: currencyFormat.format(totalReturnNoFinancing3),
                alignment: 'right',
                fontSize: 6,
                bold: true,
              },
              {
                text: currencyFormat.format(totalReturnNoFinancing5),
                alignment: 'right',
                fontSize: 6,
                bold: true,
              },
              {
                text: currencyFormat.format(totalReturnNoFinancing10),
                alignment: 'right',
                fontSize: 6,
                bold: true,
              },
            ],
            [
              {
                text: 'Cap Rate',
                alignment: 'left',
                fontSize: 6,
                bold: true,
                border: [false, true, false, false],
              },
              {
                text: '',
                alignment: 'right',
                fontSize: 6,
                bold: true,
                border: [false, true, false, false],
              },
              {
                text: totalROINoFinancing1.toFixed(2) + '%',
                alignment: 'right',
                fontSize: 6,
                bold: true,
                border: [false, true, false, false],
              },
              {
                text: totalROINoFinancing3.toFixed(2) + '%',
                alignment: 'right',
                fontSize: 6,
                bold: true,
                border: [false, true, false, false],
              },
              {
                text: totalROINoFinancing5.toFixed(2) + '%',
                alignment: 'right',
                fontSize: 6,
                bold: true,
                border: [false, true, false, false],
              },
              {
                text: totalROINoFinancing10.toFixed(2) + '%',
                alignment: 'right',
                fontSize: 6,
                bold: true,
                border: [false, true, false, false],
              },
            ],
          ],
        },
        layout: {
          defaultBorder: false,
        },
      },
      {
        margin: [15, 5, 15, 0],
        table: {
          headerRows: 1,
          widths: [150, '*', '*', '*', '*', '*'],
          body: [
            [
              {
                text: 'Returns With Financing',
                fillColor: '#3399ff',
                color: 'white',
                fontSize: 8,
                colSpan: 6,
                alignment: 'left',
              },
              { text: '' },
              { text: '' },
              { text: '' },
              { text: '' },
              { text: '' },
            ],
            [
              {
                text: 'NOI (Cash Available)',
                alignment: 'left',
                fontSize: 6,
              },
              {
                text: currencyFormat.format(NOIFinanceMonthly),
                alignment: 'right',
                fontSize: 6,
              },
              {
                text: currencyFormat.format(NOIFinance1),
                alignment: 'right',
                fontSize: 6,
              },
              {
                text: currencyFormat.format(NOIFinance3),
                alignment: 'right',
                fontSize: 6,
              },
              {
                text: currencyFormat.format(NOIFinance5),
                alignment: 'right',
                fontSize: 6,
              },
              {
                text: currencyFormat.format(NOIFinance10),
                alignment: 'right',
                fontSize: 6,
              },
            ],
            [
              {
                text: 'Mortgage',
                alignment: 'left',
                fontSize: 6,
                border: [false, false, false, true],
              },
              {
                text: currencyFormat.format(mortgagePayment),
                alignment: 'right',
                fontSize: 6,
                border: [false, false, false, true],
              },
              {
                text: currencyFormat.format(mortgagePaymentYearly),
                alignment: 'right',
                fontSize: 6,
                border: [false, false, false, true],
              },
              {
                text: currencyFormat.format(mortgagePaymentYearly),
                alignment: 'right',
                fontSize: 6,
                border: [false, false, false, true],
              },
              {
                text: currencyFormat.format(mortgagePaymentYearly),
                alignment: 'right',
                fontSize: 6,
                border: [false, false, false, true],
              },
              {
                text: currencyFormat.format(mortgagePaymentYearly),
                alignment: 'right',
                fontSize: 6,
                border: [false, false, false, true],
              },
            ],
            //[{text: 'Principal Paydown', alignment: 'left',fontSize: 6,}, {text: '$1', alignment: 'right',fontSize: 6,},{text: '$1', alignment: 'right',fontSize: 6,}, {text: '$1', alignment: 'right',fontSize: 6,}, {text: '$1', alignment: 'right',fontSize: 6,}, {text: '$1', alignment: 'right',fontSize: 6,}],
            //[{text: 'Estimated Tax Savings', alignment: 'left',fontSize: 6, }, {text: '$1', alignment: 'right',fontSize: 6,},{text: '$1', alignment: 'right',fontSize: 6,}, {text: '$1', alignment: 'right',fontSize: 6,}, {text: '$1', alignment: 'right',fontSize: 6,}, {text: '$1', alignment: 'right',fontSize: 6,}],
            [
              {
                text: 'Net Cashflow',
                alignment: 'left',
                fontSize: 6,
                bold: true,
              },
              {
                text: '',
                alignment: 'right',
                fontSize: 6,
                bold: true,
              },
              {
                text: currencyFormat.format(totalReturnFinancing1),
                alignment: 'right',
                fontSize: 6,
                bold: true,
              },
              {
                text: currencyFormat.format(totalReturnFinancing3),
                alignment: 'right',
                fontSize: 6,
                bold: true,
              },
              {
                text: currencyFormat.format(totalReturnFinancing5),
                alignment: 'right',
                fontSize: 6,
                bold: true,
              },
              {
                text: currencyFormat.format(totalReturnFinancing10),
                alignment: 'right',
                fontSize: 6,
                bold: true,
              },
            ],
            [
              {
                text: 'Cash On Cash',
                alignment: 'left',
                fontSize: 6,
                bold: true,
                border: [false, false, false, true],
              },
              {
                text: '',
                alignment: 'right',
                fontSize: 6,
                bold: true,
                border: [false, false, false, true],
              },
              {
                text: totalROIFinancing1.toFixed(2) + '%',
                alignment: 'right',
                fontSize: 6,
                bold: true,
                border: [false, false, false, true],
              },
              {
                text: totalROIFinancing3.toFixed(2) + '%',
                alignment: 'right',
                fontSize: 6,
                bold: true,
                border: [false, false, false, true],
              },
              {
                text: totalROIFinancing5.toFixed(2) + '%',
                alignment: 'right',
                fontSize: 6,
                bold: true,
                border: [false, false, false, true],
              },
              {
                text: totalROIFinancing10.toFixed(2) + '%',
                alignment: 'right',
                fontSize: 6,
                bold: true,
                border: [false, false, false, true],
              },
            ],
          ],
        },
        layout: {
          defaultBorder: false,
        },
      },
      ...(disclaimerNode ? [disclaimerNode] : []),
    ]
  }

}
